import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import AddressPageComponent, {
  ActiveListing,
  AddressPageParams,
  AddressPageServerSideProps,
  getAddressPageServerSideProps,
  PropertyAppraisalProps,
} from 'components/pages/address/public';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { ADDRESS_ROUTE } from 'components/dynamic-page/route-matchers';
import { createAddressesApi } from 'data/addresses';
import Listing, { ListingSerializableProperties } from 'data/listing';
import { SingleAddress } from '@zoocasa/go-search';
import { PriceHistory } from 'components/pages/address/public/components/price-history';
import { createZoopraisalApi } from 'data/zoopraisal';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { SOLD_LISTING_PVR_VS_HOME_ESTIMATE_FLAG } from 'constants/feature-flags';
import { ExperimentVariants } from 'utils/types';
import { GTM_CLICK_ADDRESS_PAGE_OFFLINE_ESTIMATE_BUT_VIEW, GTM_CLICK_ADDRESS_PAGE_ONLINE_ESTIMATE_BUT_VIEW, GTM_VIEW_ADDRESS_PAGE_INACTIVE_LISTING, GTM_VIEW_ADDRESS_PAGE_SOLD_LISTING, GTM_VIEW_ADDRESS_PAGE_FOR_SALE_LISTING, GTM_CLICK_VIEW_ON_ADDRESS_PAGE, GTM_CLICK_FAV_ON_ADDRESS_PAGE, GTM_VIEW_ADDRESS_PAGE_MLML, GTM_MOBILE_LISTING_PAGE_VIEW, GTM_ADDRESS_SOLD_AUTH_VIEW, GTM_ADDRESS_SOLD_NON_AUTH_VIEW, GTM_VIEW_ADDRESS_PAGE } from 'constants/events';
import { useTrackExperimentVariant } from 'hooks/use-track-experiment-variant';
import { ADDRESS_MODAL, CONNECT_WITH_AGENT_MODAL, CONNECTION_REQUEST_MOBILE_MODAL, CONNECTION_REQUEST_POPUP_MODAL, getUserFromCookies, LOGIN_REGISTRATION_MODAL, PHOTO_GALLERY_MODAL, useModalContext, useSavedListingContext, useUserContext } from 'contexts';
import { getComplianceRequirementFromCookies, shouldScrambleForProvider } from 'utils/compliance';
import { useIsomorphicLayoutEffect } from 'types/react';
import { AnalyticsController } from 'analytics/analytics-controller';
import { NEXT_TICK_TIMING, useAutoComplianceModal, useReusableComplianceModal } from 'hooks/use-compliance-modal';
import { ComplianceRequirement } from 'types/compliance';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { useIsMobile } from 'hooks/use-size-class';
import { useListingCardClick } from 'hooks/use-listing-card-click';
import { FEATURE_FLAGS, hasFeatureFlag } from 'components/pages/address/public/types';
import { captureListingView } from 'pages/api/viewed-listings/viewedListingsApiClient';
import { useScrollDetection } from 'components/atomic/organisms/sticky-tab-bar/api/use-scroll-detection';
import { useTrackPropertyView } from 'analytics/hooks/use-track-property-view';
import { standardStatusToString } from 'types/reso';
import { PROPERTY_VIEWED_SOURCE_ADDRESS_PAGE, PROPERTY_FAVOURITED_SOURCE_ADDRESS_PAGE } from '@bedrock/analytics';

// function trackGtmListingPageError(reason?: string) {
//   if (!reason) return;
//   switch (reason) {
//     case AbortReasons.TIMEOUT:
//       trackEvent(GTM_LISTING_PAGE_TIMEOUT_UI_ERROR);
//       break;
//     case AbortReasons.MANUAL:
//     case AbortReasons.ON_UNMOUNT:
//       break;
//     default:
//       console.warn(`Cannot load listing page due to unexpected reason: ${reason}`);
//       trackEvent(GTM_LISTING_PAGE_LOAD_UI_ERROR);
//   }
// }

const handler: HandlerType<AddressPageServerSideProps, AddressPageParams> = async (context, features, tenant, i18n) => {
  const addressesApi = createAddressesApi();
  const appraisalApi = createZoopraisalApi();
  // Pass the full i18n context (includes t function and resolved locale) for server-side SEO translation
  const props = await getAddressPageServerSideProps(context, features, tenant, addressesApi, appraisalApi, i18n);
  return props;
};

export const getServerSideProps = createGetServerSideProps<AddressPageServerSideProps, AddressPageParams>(handler);

export default function AddressPage(props: AddressPageServerSideProps) {
  const router = useRouter();
 
  // Use page path as key. This is so we can force page content/data to rerender when the listing changes.
  // The area listings page also uses this pattern to reload a new listing see: https://github.com/zoocasa/zoocasa-next/blob/development/src/pages/area-listings/%5B...areaPath%5D.tsx#L69-L86
  const pagePath = router.isReady ? router.asPath : router.pathname;
  const { headTags } = props.props;

  return (
    <Layout showMobileBottomNav={false} routeName={ADDRESS_ROUTE}>
      {headTags && <HeadData data={headTags} />}
      <AddressPageContent key={pagePath} props={props} />
    </Layout>
  );
}

function AddressPageContent({ props }: { props: AddressPageServerSideProps }) {
  const {
    activeListing,
    address,
    history,
    breadcrumbs,
    tenantId,
    appraisal: { appraisalResult, sellingRange },
    stats,
    featureFlags,
    compliance,
    secondaryInfo,
    listingSource,
    qaComparison,
  } = props.props;
  const { t } = useTranslation(Namespaces.COMMON);
  const isMobile = useIsMobile();
  const listingRef = useRef(Listing.fromJSON(activeListing.listing));
  const { user, acceptedTerms, acceptedBrowserBasedTerms, areZoocasaTermsRead, setZoocasaTermsStateToRead } = useUserContext();
  const { openModal } = useModalContext();
  const { savedListings, addSavedListing, removeSavedListing, isSavedListing } = useSavedListingContext();
  const setZoocasaTermsStateToReadRef = useRef(setZoocasaTermsStateToRead);
  const [listing, setListing] = useState<ActiveListing<Listing>>(() => {
    return {
      isSaved: isSavedListing(activeListing.listing.addressSlug, activeListing.listing.id),
      listing: Listing.fromJSON(activeListing?.listing),
    };
  });

  const openModalRef = useRef(openModal);
  const addSavedListingRef = useRef(addSavedListing);
  const removeSavedListingRef = useRef(removeSavedListing);
  const complianceRequirementRef = useRef(compliance?.complianceRequirement);
  const [showTermsOfUseBanner, setShowTermsOfUseBanner] = useState(compliance?.showTermsOfUseBanner);
  const [priceHistory, setPriceHistory] = useState<readonly PriceHistory<Listing>[]>(() => history.map(listing => priceHistoryFromJson(listing)).sort((a, b) => a.addedAt < b.addedAt ? 1 : -1)); //sort descending for price history component
  const electedAddress = SingleAddress.fromPartial(address);
  const openComplianceModal = useReusableComplianceModal(openModal);

  const appraisal: PropertyAppraisalProps['appraisal'] = useMemo(() => ({
    appraisalResult,
    sellingRange,
    onContactAnAgentClick: () => openModalRef.current?.(CONNECT_WITH_AGENT_MODAL, { listingId: listingRef.current?.id, source: 'CMA' }),
  }), [appraisalResult, sellingRange]);


  const [complianceRequirement, setComplianceRequirement] = useState(compliance.complianceRequirement);
  const [shouldScramble, setShouldScramble] = useState(compliance.shouldScramble);

  /* whenever the user accepts terms or logs in, we need to recalculate the compliance requirement */
  useIsomorphicLayoutEffect(() => {
    const newComplianceRequirement = getComplianceRequirementFromCookies(listingRef.current.providerId, user?.id);
    const hasActiveComplianceChanged = newComplianceRequirement !== complianceRequirementRef.current;

    // Keep active listing compliance in sync for page-level private/scramble behavior.
    if (hasActiveComplianceChanged) {
      setComplianceRequirement(newComplianceRequirement);
      const newShouldScramble = shouldScrambleForProvider(listingRef.current.providerId, newComplianceRequirement);
      setShouldScramble(newShouldScramble);
      complianceRequirementRef.current = newComplianceRequirement;
    }

    // Also refresh compliance on every history item (provider-specific), so price history
    // blur/disclaimer updates after login/terms changes even when active listing compliance is unchanged.
    setPriceHistory((prevState: readonly PriceHistory<Listing>[]) =>
    {
      const hasChanged = prevState.some(listing => listing.complianceRequirement !== getComplianceRequirementFromCookies(listing.providerId, user?.id));
      if (!hasChanged) return prevState;
      return prevState.map(listing => (
        { ...listing,
          complianceRequirement: getComplianceRequirementFromCookies(listing.providerId, user?.id),
        }
      ));
    });
  }, [acceptedTerms, acceptedBrowserBasedTerms, user?.id]);

  useIsomorphicLayoutEffect(() => {
    setShowTermsOfUseBanner(listingRef.current.isCrea && !areZoocasaTermsRead);
  }, [areZoocasaTermsRead]);

  /** Analytics */
  useIsomorphicLayoutEffect(() => {
    AnalyticsController.trackEvent(GTM_VIEW_ADDRESS_PAGE);
    // Check listing status and trigger corresponding GTM event
    AnalyticsController.trackEventConditionally(
      { event: GTM_VIEW_ADDRESS_PAGE_SOLD_LISTING },
      listingRef.current.isSold
    );
    AnalyticsController.trackEventConditionally(
      { event: GTM_VIEW_ADDRESS_PAGE_FOR_SALE_LISTING },
      listingRef.current.isAvailable
    );
    AnalyticsController.trackEventConditionally(
      { event: GTM_VIEW_ADDRESS_PAGE_INACTIVE_LISTING },
      listingRef.current.isInactive
    );
    AnalyticsController.trackEventConditionally({ event: GTM_VIEW_ADDRESS_PAGE_INACTIVE_LISTING }, listingRef.current.isInactive);

    AnalyticsController.trackEventConditionally({ event: GTM_VIEW_ADDRESS_PAGE_MLML }, hasFeatureFlag(featureFlags.flags, FEATURE_FLAGS.MY_LINK_MY_LEAD_ENABLED));

    if (listingRef.current.isCrea) {
      AnalyticsController.trackCreaViewEvent(listingRef.current.id, listingRef.current?.url);
    }

    AnalyticsController.trackEventConditionally({ event: GTM_MOBILE_LISTING_PAGE_VIEW }, isMobile);

    AnalyticsController.trackEventConditionally(
      { event: GTM_ADDRESS_SOLD_AUTH_VIEW },
      () => {
        const user = getUserFromCookies();
        return user?.id !== undefined && listingRef.current.isSold;
      }
    );

    AnalyticsController.trackEventConditionally(
      { event: GTM_ADDRESS_SOLD_NON_AUTH_VIEW },
      () => {
        const user = getUserFromCookies();
        return user?.id === undefined && listingRef.current.isSold;
      }
    );
  }, []);

  /** Track property viewed event when the listing is viewed */
  // eslint-disable-next-line react-hooks/refs -- pre-existing pattern predating the @bedrock/analytics migration; reads the ref synchronously to fire a one-time tracking call, not for render output
  useTrackPropertyView(listingRef.current.id, listingRef.current.addressSlug, listingRef.current.standardStatus, PROPERTY_VIEWED_SOURCE_ADDRESS_PAGE);

  // Capture viewed listing when page loads
  useIsomorphicLayoutEffect(() => {
    const listing = listingRef.current;
    const listingId = listing.id;
    const listingSlug = listing.addressSlug || '';
    const canCaptureListingView = listingId && listingId > 0 && listingSlug && listingSlug.length > 0 && user?.id;
    if (canCaptureListingView) {
      captureListingView(listingId, user.id, false, tenantId, listingSlug);
    }
  }, [user?.id, tenantId]);

  // If there is a compliance requirement, open the compliance modal on the next tick.
  // eslint-disable-next-line react-hooks/refs -- pre-existing pattern predating the @bedrock/analytics migration; reads the ref synchronously as hook arguments, not for render output
  useAutoComplianceModal({
    complianceRequirement: complianceRequirement,
    // eslint-disable-next-line react-hooks/refs -- pre-existing pattern predating the @bedrock/analytics migration; reads the ref synchronously as a hook argument, not for render output
    providerId: listingRef.current.providerId,
    openModal,
    timing: NEXT_TICK_TIMING,
  });

  useEffect(() => {
    const isSaved = isSavedListing(listingRef.current.addressSlug, listingRef.current.id);
    setListing(prevState => {
      if (isSaved !== prevState.isSaved) {
        return { ...prevState, isSaved };
      }
      return prevState;
    });
  }, [savedListings, isSavedListing]);

  const abSoldListingExperiment = useFeatureValue(SOLD_LISTING_PVR_VS_HOME_ESTIMATE_FLAG, ExperimentVariants.A);

  // Track experiment variant for SOLD listings only on address page
  // eslint-disable-next-line react-hooks/refs -- pre-existing pattern predating the @bedrock/analytics migration; reads the ref synchronously as hook arguments, not for render output
  useTrackExperimentVariant({
    variant: abSoldListingExperiment,
    eventA: GTM_CLICK_ADDRESS_PAGE_ONLINE_ESTIMATE_BUT_VIEW,
    eventB: GTM_CLICK_ADDRESS_PAGE_OFFLINE_ESTIMATE_BUT_VIEW,
    // eslint-disable-next-line react-hooks/refs -- pre-existing pattern predating the @bedrock/analytics migration; reads the ref synchronously as a hook argument, not for render output
    isActive: listingRef.current.isSold,
  });

  /*
   * This will be called when the user dismisses the Zoocasa terms of use banner.
   */
  const handleTermsOfUseBannerDismiss = useCallback(() => {
    setZoocasaTermsStateToReadRef.current();
  }, []);

  /*
   * This will be called when the user clicks on any of the compliance requirement
   * buttons and it will open the appropriate compliance modal based on
   * the compliance requirement.
   */
  const handleOnComplianceRequirementClick = useCallback((complianceRequirement: ComplianceRequirement, providerId: number) => {
    openComplianceModal(complianceRequirement, providerId);
  }, [openComplianceModal]);

  /*
   * This will be called when the user clicks the "See all saved listings"
   * button.
   */
  const handleSeeAllSavedListingsClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_VIEW_ON_ADDRESS_PAGE);
  }, []);

  const handleSaveListingClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_FAV_ON_ADDRESS_PAGE);
    const user = getUserFromCookies();
    if (user) {
      const currentListing = listingRef.current;
      const isCurrentlySaved = isSavedListing(currentListing.addressSlug, currentListing.id);
      if (isCurrentlySaved) {
        removeSavedListingRef.current(currentListing.id, { source: PROPERTY_FAVOURITED_SOURCE_ADDRESS_PAGE });
      } else {
        addSavedListingRef.current({
          listingId: currentListing.id,
          isModal: false,
          source: PROPERTY_FAVOURITED_SOURCE_ADDRESS_PAGE,
          ...(currentListing.standardStatus != null && { standard_status: standardStatusToString(currentListing.standardStatus) }),
        });
      }
    } else {
      openModalRef.current?.(LOGIN_REGISTRATION_MODAL);
    }
  }, [isSavedListing]);

  /*
   * This will be called when the user clicks on any item from the price history.
   * skipDiscrepancyCheck is true because the listingId is a historical entry that
   * may not match the active listing in go-search — this is expected, not a discrepancy.
   */
  const handleViewListingClick = useCallback((addressPath: string, listingId: number) => {
    openModalRef.current?.(ADDRESS_MODAL, { addressPath, listingId, skipDiscrepancyCheck: true });
  }, []);

  /* This will be called when the user clicks the "Contact for Price" button on the price history row */
  const handleContactForPriceClick = useCallback(() => {
    const prefillData = {
      requestViewing: false,
      isShowingAdditionalComments: true,
      sourceComments: t(LOCALE_COMMON_PATHS.PRICE_HISTORY_ROW_CONTACT_FOR_PRICE_COMMENT,
        {
          defaultValue: 'I\'d like to know the price of this property.',
        }),
    };
    const customButtonText = t(LOCALE_COMMON_PATHS.PRICE_HISTORY_ROW_REQUEST_INFO, { defaultValue: 'Request Info' });
    openModalRef.current?.(isMobile ? CONNECTION_REQUEST_MOBILE_MODAL : CONNECTION_REQUEST_POPUP_MODAL, {
      listing: listingRef.current,
      isViewingRequest: true,
      customButtonText,
      prefillData: prefillData,
    });
  }, [isMobile, t]);

  /*
   * This will be called when the user clicks on any image or the map in the photo gallery carousel.
   */
  const handleSwiperCarouselImageClick = useCallback((index: number | 'map'): void => {
    openModalRef.current?.(PHOTO_GALLERY_MODAL, { listing: listingRef.current, photoIndex: index, isModal: false });
  }, []);

  /*
   * This will be called when the user clicks on the "Show Details" button in the rooms section.
   */
  const handleRoomsShowDetailsClick = useCallback(() => {
    openComplianceModal(complianceRequirement, listingRef.current.providerId);
  }, [complianceRequirement, openComplianceModal]);

  /*
   * This will be called when the user clicks on a similar listing card.
   */
  const handleSimilarListingsCardClick = useListingCardClick();

  useScrollDetection('window');

  return (
    <AddressPageComponent
      address={electedAddress}
      activeListing={listing}
      history={priceHistory}
      breadcrumbs={breadcrumbs}
      tenantId={tenantId}
      appraisal={appraisal}
      stats={stats}
      secondaryInfo={secondaryInfo}
      featureFlags={featureFlags}
      compliance={{
        showTermsOfUseBanner,
        shouldScramble,
        onTermsOfUseBannerDismiss: handleTermsOfUseBannerDismiss,
        complianceRequirement: complianceRequirement,
        onComplianceRequirementClick: handleOnComplianceRequirementClick,
        hideSquareFootage: compliance.hideSquareFootage,
      }}
      experiments={{
        // eslint-disable-next-line react-hooks/refs -- pre-existing pattern predating the @bedrock/analytics migration; reads the ref synchronously for a prop value, not for its own render output
        soldListingExperimentId: listingRef.current.isSold ? abSoldListingExperiment : null,
      }}
      onSeeAllSavedListingsClick={handleSeeAllSavedListingsClick}
      onSaveListingClick={handleSaveListingClick}
      onContactForPriceClick={handleContactForPriceClick}
      onViewListingClick={handleViewListingClick}
      onSwiperCarouselImageClick={handleSwiperCarouselImageClick}
      onRoomsShowDetailsClick={handleRoomsShowDetailsClick}
      onSimilarListingsCardClick={handleSimilarListingsCardClick}
      listingSource={listingSource}
      qaComparison={qaComparison}
    />
  );
}

function priceHistoryFromJson(json: PriceHistory<ListingSerializableProperties>): PriceHistory<Listing> {
  return {
    ...json,
    addedAt: json.addedAt != null ? new Date(json.addedAt) : null,
    expiredAt: json.expiredAt != null ? new Date(json.expiredAt) : null,
    soldAt: json.soldAt != null ? new Date(json.soldAt) : null,
  };
}
